import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, Search, User, Heart, ArrowRight } from 'lucide-react';
import { useCart } from '../../lib/cartContext';
import { useAuth } from '../../lib/AuthContext';
import { useSaved } from '../../lib/SavedContext';
import { TOKENS, limeBtn, displayFont } from '../../lib/designTokens';
import { GlobalSearch } from './GlobalSearch';

const LOGO_URL = "/ninjathlete-wordmark.png";

// CRO Audit §5.2 — primary nav anchors (Shop promoted per user request).
const PRIMARY_NAV = [
  { href: '/shop', label: 'Shop' },
  { href: '/peptides', label: 'Peptides' },
  { href: '/peptide-subscription', label: 'Membership' },
  { href: '/hyrox', label: 'HYROX' },
  {
    href: '/recovery', label: 'Recovery',
    children: [
      { href: '/recovery', label: 'Recovery Hub' },
      { href: '/hydrotherapy', label: 'Oxygen Nanobubble Hydrotherapy' },
    ],
  },
  { href: '/locator', label: 'Locator' },
  { href: '/longevity', label: 'Longevity' },
  { href: '/tools', label: 'Tools' },
];

// Secondary links — kept accessible via mobile + footer, removed from primary nav.
const SECONDARY_NAV = [
  { href: '/blog', label: 'Blog' },
  { href: '/guides', label: 'Guides' },
  { href: '/series', label: 'Series' },
  { href: '/city-series', label: 'City Series Drop' },
  { href: '/womens-collection', label: "Women's Collection Drop" },
  { href: '/affiliates', label: 'Affiliates' },
  { href: '/community', label: 'Community' },
  { href: '/contact', label: 'Contact' },
];

export const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { itemCount, openCart } = useCart();
  const { user } = useAuth();
  const { savedIds } = useSaved();
  const location = useLocation();

  const wishlistCount = user ? [...savedIds].filter(k => k.startsWith('product:')).length : 0;
  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setSearchOpen(true); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Scroll to dojo capture (anchor #dojo) — falls back to navigating home if not on /
  const handleDojoClick = (e) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (location.pathname === '/') {
      const el = document.querySelector('[data-testid="lead-magnet-section"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      window.location.href = '/#dojo';
    }
  };

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 glass-panel"
      style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
      data-testid="main-navigation"
    >
      <div className="container-ninja">
        <div className="flex items-center justify-between h-16 md:h-20 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group flex-shrink-0" data-testid="nav-logo">
            <img src={LOGO_URL} alt="NinjAthlete" className="h-6 md:h-7 xl:h-8 w-auto invert" />
          </Link>

          {/* Primary Desktop Nav — 5 anchors only */}
          <div className="hidden lg:flex items-center gap-6 flex-1 justify-center">
            {PRIMARY_NAV.map((link) => {
              const active = isActive(link.href);
              if (link.children) {
                return (
                  <div key={link.href} className="relative group">
                    <Link
                      to={link.href}
                      className="text-sm uppercase transition-all duration-200 relative py-1 inline-block"
                      style={{
                        ...displayFont,
                        letterSpacing: '0.15em',
                        color: active ? TOKENS.lime : '#A1A1AA',
                      }}
                      onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = '#fff'; }}
                      onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = '#A1A1AA'; }}
                      data-testid={`nav-link-${link.label.toLowerCase()}`}
                    >
                      {link.label}
                      {active && (
                        <span className="absolute -bottom-1 left-0 right-0 h-px" style={{ backgroundColor: TOKENS.lime }} />
                      )}
                    </Link>
                    <div
                      className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50"
                      data-testid={`nav-dropdown-${link.label.toLowerCase()}`}
                    >
                      <div
                        className="min-w-[260px] py-2 border"
                        style={{ backgroundColor: '#101010', borderColor: 'rgba(255,255,255,0.1)' }}
                      >
                        {link.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className="block px-4 py-2.5 text-xs uppercase transition-colors hover:text-white"
                            style={{
                              ...displayFont,
                              letterSpacing: '0.12em',
                              color: isActive(child.href) && child.href !== link.href ? TOKENS.lime : '#A1A1AA',
                            }}
                            data-testid={`nav-sublink-${child.href.replace(/\//g, '')}`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm uppercase transition-all duration-200 relative py-1"
                  style={{
                    ...displayFont,
                    letterSpacing: '0.15em',
                    color: active ? TOKENS.lime : '#A1A1AA',
                  }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = '#A1A1AA'; }}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                  {active && (
                    <span
                      className="absolute -bottom-1 left-0 right-0 h-px"
                      style={{ backgroundColor: TOKENS.lime }}
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Right cluster: Search, Dojo CTA, Account, Cart */}
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            <button
              onClick={() => setSearchOpen(true)}
              className="hidden md:flex items-center gap-2 px-3 py-1.5 border text-zinc-500 hover:text-white transition-all duration-300 text-xs"
              style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderColor: 'rgba(255,255,255,0.1)' }}
              data-testid="nav-search-btn"
            >
              <Search className="w-3.5 h-3.5" />
              <span style={displayFont}>Search</span>
              <kbd className="ml-1 px-1.5 py-0.5 text-[10px] rounded" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>⌘K</kbd>
            </button>
            <button
              onClick={() => setSearchOpen(true)}
              className="md:hidden p-2 text-zinc-400 hover:text-white transition-colors"
              data-testid="nav-search-btn-mobile"
              aria-label="Search"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Persistent Dojo CTA — CRO Audit §3.3 #8 */}
            <button
              onClick={handleDojoClick}
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 text-xs uppercase transition-all duration-300 hover:-translate-y-px hover:shadow-[0_6px_24px_-6px_rgba(199,250,62,0.5)]"
              style={limeBtn}
              data-testid="nav-join-dojo-btn"
            >
              Join the Dojo <ArrowRight className="w-3.5 h-3.5" />
            </button>

            {/* Account */}
            <Link
              to={user ? '/account' : '/auth'}
              className="hidden md:flex p-2 text-zinc-400 hover:text-white transition-colors"
              style={user ? { color: TOKENS.lime } : {}}
              data-testid="nav-account-btn"
              aria-label={user ? 'Account' : 'Sign in'}
            >
              <User className="w-5 h-5" />
            </Link>

            {/* Wishlist */}
            <Link
              to={user ? '/wishlist' : '/auth'}
              className="hidden md:flex relative p-2 text-zinc-400 hover:text-white transition-colors"
              data-testid="nav-wishlist-btn"
              aria-label="Wishlist"
            >
              <Heart className="w-5 h-5" style={wishlistCount > 0 ? { color: TOKENS.lime } : {}} />
              {wishlistCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 w-4 h-4 text-[10px] flex items-center justify-center rounded-full"
                  style={{ backgroundColor: TOKENS.lime, color: TOKENS.canvas }}
                >
                  {wishlistCount}
                </span>
              )}
            </Link>

            {/* Cart */}
            <button
              onClick={openCart}
              className="relative p-2 text-zinc-400 hover:text-white transition-colors"
              data-testid="cart-button"
              aria-label="Open cart"
            >
              <ShoppingBag className="w-5 h-5" />
              {itemCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 w-5 h-5 text-xs flex items-center justify-center rounded-full"
                  style={{ backgroundColor: TOKENS.lime, color: TOKENS.canvas }}
                >
                  {itemCount}
                </span>
              )}
            </button>

            {/* Mobile menu */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-zinc-400 hover:text-white transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden py-5 max-h-[80vh] overflow-y-auto"
            style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
            data-testid="mobile-menu"
          >
            {/* Mobile Dojo CTA */}
            <button
              onClick={handleDojoClick}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 mb-5 text-sm uppercase tracking-wider"
              style={limeBtn}
              data-testid="mobile-join-dojo-btn"
            >
              Join the Dojo <ArrowRight className="w-4 h-4" />
            </button>

            <div className="flex flex-col gap-1">
              <p
                className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 px-1 mb-2 mt-2"
                style={displayFont}
              >
                Pillars
              </p>
              {PRIMARY_NAV.map((link) => (
                <React.Fragment key={link.href}>
                  <Link
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-sm uppercase py-2.5 px-1 transition-colors"
                    style={{
                      ...displayFont,
                      letterSpacing: '0.15em',
                      color: isActive(link.href) ? TOKENS.lime : '#D4D4D8',
                    }}
                  >
                    {link.label}
                  </Link>
                  {(link.children || []).filter((c) => c.href !== link.href).map((child) => (
                    <Link
                      key={child.href}
                      to={child.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-xs uppercase py-2 pl-5 px-1 transition-colors"
                      style={{
                        ...displayFont,
                        letterSpacing: '0.12em',
                        color: isActive(child.href) ? TOKENS.lime : '#A1A1AA',
                      }}
                      data-testid={`nav-mobile-sublink-${child.href.replace(/\//g, '')}`}
                    >
                      {child.label}
                    </Link>
                  ))}
                </React.Fragment>
              ))}

              <p
                className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 px-1 mb-2 mt-5"
                style={displayFont}
              >
                More
              </p>
              {SECONDARY_NAV.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-xs uppercase py-2 px-1 transition-colors text-zinc-400 hover:text-white"
                  style={{ ...displayFont, letterSpacing: '0.15em' }}
                >
                  {link.label}
                </Link>
              ))}

              <div
                className="grid grid-cols-2 gap-2 mt-5 pt-5"
                style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
              >
                <Link
                  to={user ? '/account' : '/auth'}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-xs uppercase py-2 px-3 flex items-center gap-2 text-zinc-300"
                  style={{ ...displayFont, backgroundColor: 'rgba(255,255,255,0.04)' }}
                  data-testid="mobile-account-link"
                >
                  <User className="w-4 h-4" /> {user ? 'Account' : 'Sign In'}
                </Link>
                <Link
                  to={user ? '/wishlist' : '/auth'}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-xs uppercase py-2 px-3 flex items-center gap-2 text-zinc-300"
                  style={{ ...displayFont, backgroundColor: 'rgba(255,255,255,0.04)' }}
                  data-testid="mobile-wishlist-link"
                >
                  <Heart className="w-4 h-4" /> Wishlist {wishlistCount > 0 && `(${wishlistCount})`}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
      <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </nav>
  );
};

export default Navigation;
